import React, { createContext, useContext, useState } from 'react';
import authApi from '../api/authApi';
import { useOnce } from '../hooks/useOnce';

const AuthContext = createContext();

// A password-verified login awaiting its OTP. Holds only the opaque
// challenge token (never the OTP itself), in sessionStorage so a refresh of
// the verification page keeps working but it dies with the tab.
const PENDING_2FA_KEY = 'pending2fa';

const readPendingTwoFactor = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_2FA_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writePendingTwoFactor = (value) => {
  try {
    if (value) sessionStorage.setItem(PENDING_2FA_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(PENDING_2FA_KEY);
  } catch (e) {
    // storage unavailable - the in-memory state still works for this page
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingTwoFactor, setPendingTwoFactor] = useState(readPendingTwoFactor);

  const updatePendingTwoFactor = (value) => {
    writePendingTwoFactor(value);
    setPendingTwoFactor(value);
  };

  const completeLogin = (token, userData) => {
    localStorage.setItem('token', token);
    updatePendingTwoFactor(null);
    setUser(userData);
  };

  // Debug user state changes
  React.useEffect(() => {
    }, [user]);

  useOnce(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token with backend
      authApi.verify()
      .then(response => {
        if (response.data.success) {
          setUser(response.data.user);
          } else {
          localStorage.removeItem('token');
        }
      })
      .catch((error) => {
        // Only clear token if it's a 401 error (invalid/expired token)
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
        } else {
          // Don't clear token on network errors - user might still be logged in
        }
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (username, password, captchaId, captcha) => {
    try {
      const response = await authApi.login(username, password, captchaId, captcha);
      if (response.data.success && response.data.two_factor_required) {
        updatePendingTwoFactor({
          challengeToken: response.data.challenge_token,
          maskedEmail: response.data.masked_email,
          username,
          otpExpiresAt: Date.now() + response.data.otp_expires_in * 1000,
          resendAvailableAt: Date.now() + response.data.resend_available_in * 1000
        });
        return { success: true, twoFactorRequired: true, message: response.data.message };
      }
      if (response.data.success) {
        const { token, user: userData } = response.data;
        completeLogin(token, userData);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const verifyOtp = async (otp) => {
    if (!pendingTwoFactor) {
      return { success: false, code: 'INVALID_CHALLENGE', message: 'Your verification session has expired. Please sign in again.' };
    }
    try {
      const response = await authApi.verifyOtp(pendingTwoFactor.challengeToken, otp);
      completeLogin(response.data.token, response.data.user);
      return { success: true };
    } catch (error) {
      const data = error.response?.data || {};
      if (data.code === 'INVALID_CHALLENGE') updatePendingTwoFactor(null);
      return {
        success: false,
        code: data.code,
        message: data.message || 'Verification failed. Please try again.',
        attemptsRemaining: data.attempts_remaining
      };
    }
  };

  const resendOtp = async () => {
    if (!pendingTwoFactor) {
      return { success: false, code: 'INVALID_CHALLENGE', message: 'Your verification session has expired. Please sign in again.' };
    }
    try {
      const response = await authApi.resendOtp(pendingTwoFactor.challengeToken);
      updatePendingTwoFactor({
        ...pendingTwoFactor,
        maskedEmail: response.data.masked_email || pendingTwoFactor.maskedEmail,
        otpExpiresAt: Date.now() + response.data.otp_expires_in * 1000,
        resendAvailableAt: Date.now() + response.data.resend_available_in * 1000
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      const data = error.response?.data || {};
      if (data.code === 'INVALID_CHALLENGE' || data.code === 'RESEND_LIMIT') updatePendingTwoFactor(null);
      if (data.code === 'RESEND_TOO_SOON' && data.retry_after) {
        updatePendingTwoFactor({ ...pendingTwoFactor, resendAvailableAt: Date.now() + data.retry_after * 1000 });
      }
      return { success: false, code: data.code, message: data.message || 'Could not resend the code. Please try again.' };
    }
  };

  const cancelTwoFactor = () => updatePendingTwoFactor(null);

  // Changing the password revokes every session, including this one; the
  // backend returns a fresh token so this tab stays signed in.
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      if (response.data.token) localStorage.setItem('token', response.data.token);
      return { success: true, message: response.data.message };
    } catch (error) {
      const data = error.response?.data || {};
      return { success: false, field: data.field, message: data.message || 'Unable to change password. Please try again.' };
    }
  };

  const logout = () => {
    // Record the logout server-side (audit log). Fire-and-forget: the user
    // is logged out locally whether or not this request succeeds.
    const currentToken = localStorage.getItem('token');
    if (currentToken) authApi.logout(currentToken).catch(() => {});

    // Clear token from localStorage
    localStorage.removeItem('token');
    // Clear any other auth-related data from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('token') || key.includes('auth') || key.includes('user'))) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      });
    
    // Clear user state
    setUser(null);
    // Verify token is cleared
    };

  const updateUser = (updatedUser) => {
    setUser(prev => ({
      ...prev,
      ...updatedUser
    }));
  };

  const refreshUser = async () => {
    try {
      const response = await authApi.verify();

      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
      }
  };

  const checkAuthStatus = () => {
    const token = localStorage.getItem('token');
    return !!token && !!user;
  };

  const value = {
    user,
    login,
    logout,
    pendingTwoFactor,
    verifyOtp,
    resendOtp,
    cancelTwoFactor,
    changePassword,
    updateUser,
    refreshUser,
    checkAuthStatus,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
